/**
 * The create panel's call link, under „Rozmowa wideo" (owner, 28 October 2026): Google Meet or the
 * trainer's own link where Google makes a Meet, the link field alone elsewhere — the standing link
 * prefilled — with the platform's suggestions under it. What stands is `model/callLinkChoice`'s.
 */

import { useTranslation } from 'react-i18next';

import { FormField } from '~/shared/ui/FormField';
import { SegmentedChoice } from '~/shared/ui/SegmentedChoice';

import type { CallLinkChoiceView, CallLinkSourceChoice } from '../model/callLinkChoice';
import styles from './ComposeForm.module.css';

interface CallLinkChoiceProps {
  readonly choice: CallLinkChoiceView;
  readonly source: CallLinkSourceChoice;
  readonly text: string;
  readonly error: string | null;
  readonly onSource: (source: CallLinkSourceChoice) => void;
  readonly onText: (text: string) => void;
}

export function CallLinkChoice({ choice, source, text, error, onSource, onText }: CallLinkChoiceProps) {
  const { t } = useTranslation();
  const meet = choice.sources !== null && source === 'meet';

  return (
    // The link joins whoever holds it (consultation-calls R7): kept out of session replay.
    <div className={styles.videoCall} data-clarity-mask="True">
      {choice.sources === null ? null : (
        <SegmentedChoice
          legend={t('web:booking.create.sourceLegend')}
          options={choice.sources.map((value) => ({
            value,
            label: t(value === 'meet' ? 'web:booking.create.sourceMeet' : 'web:booking.create.sourceOwn'),
          }))}
          value={source}
          onChange={onSource}
        />
      )}
      {meet ? (
        <p className={styles.videoCallNote}>{t('web:booking.create.videoCallAuto')}</p>
      ) : (
        <>
          {/* The field a refused send takes focus to (`DRAFT_PROBLEM_FIELD`). */}
          <div data-field="callLink">
            <FormField
              label={t('web:booking.call.fieldLabel')}
              labelNote={choice.sources === null ? t('web:booking.call.optional') : undefined}
              value={text}
              placeholder="https://"
              error={error}
              onChange={onText}
            />
          </div>
          <p className={styles.videoCallNote}>
            {[choice.helperKey, choice.connectHintKey]
              .filter((key): key is string => key !== null)
              .map((key) => t(key))
              .join(' ')}
          </p>
        </>
      )}
    </div>
  );
}
